import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { ArrowRight, BookOpen, Inbox, type LucideIcon } from 'lucide-react';
import { api } from '@/api/client';
import { Score } from '@/components/Score';
import { ExamSurfaceDisclaimer } from '@/components/ExamSurfaceDisclaimer';
import { Pill, Tag } from '@/components/ui/Badge';
import { BandValue, formatBand } from '@/components/ui/BandScore';
import { Button, buttonClassName } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Icon } from '@/components/ui/Icon';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState, Skeleton, SkeletonCard } from '@/components/ui/States';
import { BandTrend } from '@/features/progress/BandTrend';
import { RecommendationCard } from '@/features/progress/RecommendationCard';
import { PRACTICE_SECTION_META } from '@/features/practice/practice-sections';
import { DashboardStatus } from '@/features/dashboard/DashboardStatus';
import { useIsStaff } from '@/features/admin-content/use-current-user';
import { authClient } from '@/lib/auth-client';
import { examKeyFromFamilySlug, useTheme } from '@/lib/theme';

export const Route = createFileRoute('/dashboard')({
  component: DashboardPage,
});

const MODULES = ['listening', 'reading', 'writing', 'speaking'] as const;
type ModuleKey = (typeof MODULES)[number];

const MODULE_TONE: Record<ModuleKey, string> = {
  listening: 'text-listening',
  reading: 'text-reading',
  writing: 'text-writing',
  speaking: 'text-speaking',
};

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ');
}

function DashboardPage() {
  const navigate = useNavigate();
  const { setExam } = useTheme();
  const { data: session, isPending } = authClient.useSession();
  const staff = useIsStaff();

  const attemptsQuery = useQuery({
    queryKey: ['attempts'],
    enabled: Boolean(session),
    queryFn: () => api.get('/api/v1/attempts'),
  });

  const progressQuery = useQuery({
    queryKey: ['progress-overview'],
    enabled: Boolean(session),
    queryFn: () => api.get('/api/v1/progress/overview'),
  });

  const profileQuery = useQuery({
    queryKey: ['me-profile'],
    enabled: Boolean(session),
    queryFn: () => api.get('/api/v1/me/profile'),
  });

  useEffect(() => {
    const family = progressQuery.data?.variant.familySlug;
    setExam(examKeyFromFamilySlug(family));
  }, [progressQuery.data?.variant.familySlug, setExam]);

  // First visit: students choose an exam. Staff skip this so they are not stuck on onboarding.
  useEffect(() => {
    if (staff) return;
    if (profileQuery.data && !profileQuery.data.onboarded) {
      void navigate({ to: '/onboarding' });
    }
  }, [staff, profileQuery.data, navigate]);

  /** Every dated score across all skills, oldest first. */
  const trend = useMemo(() => {
    const skills = progressQuery.data?.skills ?? {};
    return Object.values(skills)
      .flatMap((skill) => skill.history)
      .map((point) => ({ submittedAt: point.submittedAt, value: point.score.value }))
      .sort((a, b) => a.submittedAt.localeCompare(b.submittedAt));
  }, [progressQuery.data]);

  if (isPending) {
    return (
      <div className="grid gap-6">
        <Skeleton className="h-10 w-64" />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (!session) {
    return (
      <Card padding="lg" className="grid max-w-prose gap-4">
        <h1 className="font-display text-h2">Log in to see your dashboard</h1>
        <p className="text-body text-ink-2">
          Your band estimates, history and what to practise next all live here.
        </p>
        <div className="flex gap-2.5">
          <Button onClick={() => void navigate({ to: '/sign-in' })}>Log in</Button>
          <Button variant="secondary" onClick={() => void navigate({ to: '/sign-up' })}>
            Create an account
          </Button>
        </div>
      </Card>
    );
  }

  const attempts = attemptsQuery.data?.attempts ?? [];
  const recommendations = progressQuery.data?.topRecommendations ?? [];
  const skills = progressQuery.data?.skills ?? {};

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow={progressQuery.data?.variant.name}
        title={`Hello, ${session.user.name}`}
        description="See where you are against your target, and what to practise next."
        actions={
          <Link
            to="/practice"
            search={{ variant: undefined, section: 'mocks' }}
            className={buttonClassName({ size: 'lg' })}
          >
            <Icon icon={BookOpen} size={20} className="mr-2" />
            Start a test
          </Link>
        }
      />

      <DashboardStatus
        examDate={profileQuery.data?.examDate ?? null}
        timezone={profileQuery.data?.timezone ?? 'Pacific/Auckland'}
        targetScore={profileQuery.data?.targetScore ?? null}
      />

      <section aria-labelledby="dashboard-bands" className="grid gap-4">
        <h2 id="dashboard-bands" className="font-display text-h3">
          Your band estimates
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {MODULES.map((module) => (
            <ModuleBandCard
              key={module}
              module={module}
              loading={progressQuery.isPending}
              latest={skills[module]?.latest ?? null}
              attempts={skills[module]?.attempts ?? 0}
              target={profileQuery.data?.targetScore ?? null}
            />
          ))}
        </div>
      </section>

      {trend.length >= 2 ? (
        <Card padding="lg" className="grid gap-4">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="font-display text-h3">Band over time</h2>
            <Link
              to="/progress"
              search={{ variant: undefined }}
              className="text-small font-bold text-primary hover:underline"
            >
              See all progress
            </Link>
          </div>
          <BandTrend points={trend} />
        </Card>
      ) : null}

      <section aria-labelledby="dashboard-recommendations" className="grid gap-4">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 id="dashboard-recommendations" className="font-display text-h3">
            Recommended next
          </h2>
          <Link
            to="/progress"
            search={{ variant: undefined }}
            className="text-small font-bold text-primary hover:underline"
          >
            See all progress
          </Link>
        </div>

        {progressQuery.data ? (
          <ExamSurfaceDisclaimer text={progressQuery.data.variant.trademarkDisclaimer} />
        ) : null}

        {progressQuery.isPending ? (
          <div className="grid gap-4 md:grid-cols-2">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : recommendations.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title="No recommendations yet"
            description="Finish one scored test and we will tell you what to work on next."
            action={
              <Link
                to="/practice"
                search={{ variant: undefined, section: 'reading' }}
                className={buttonClassName()}
              >
                Browse practice
              </Link>
            }
          />
        ) : (
          <ul className="grid gap-4 md:grid-cols-2">
            {recommendations.map((recommendation) => (
              <li key={recommendation.id}>
                <RecommendationCard recommendation={recommendation} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="dashboard-attempts" className="grid gap-4">
        <h2 id="dashboard-attempts" className="font-display text-h3">
          Your tests
        </h2>

        {attemptsQuery.isPending ? (
          <SkeletonCard />
        ) : attemptsQuery.isError ? (
          <Card padding="lg">
            <p className="text-body text-danger">
              We could not load your tests. Reload the page to try again.
            </p>
          </Card>
        ) : attempts.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No tests yet"
            description="Your finished tests appear here with your band and feedback on every answer."
            action={
              <Link
                to="/practice"
                search={{ variant: undefined, section: 'reading' }}
                className={buttonClassName()}
              >
                Start a practice test
              </Link>
            }
          />
        ) : (
          <Card padding="none">
            <ul className="divide-y divide-border">
              {attempts.map((row) => {
                const unfinished = row.status === 'in_progress' || row.status === 'created';
                return (
                  <li key={row.attemptId}>
                    <Link
                      to={unfinished ? '/attempts/$attemptId' : '/attempts/$attemptId/results'}
                      params={{ attemptId: row.attemptId }}
                      className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 no-underline hover:bg-paper"
                    >
                      <div className="min-w-0 space-y-1">
                        <p className="font-bold text-ink">{row.blueprintSlug}</p>
                        <div className="flex flex-wrap items-center gap-2">
                          <Tag tone={unfinished ? 'warning' : 'success'}>
                            {formatStatus(row.status)}
                          </Tag>
                        </div>
                        {row.summary ? <Score score={row.summary} size="sm" /> : null}
                      </div>
                      <span className="inline-flex items-center gap-1 text-small font-bold text-primary">
                        {unfinished ? 'Open' : 'See feedback'}
                        <Icon icon={ArrowRight} size={16} />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}
      </section>
    </div>
  );
}

function ModuleBandCard({
  module,
  latest,
  attempts,
  target,
  loading,
}: {
  module: ModuleKey;
  latest: { value: number; kind: string; range: [number, number] | null } | null;
  attempts: number;
  target: number | null;
  loading: boolean;
}) {
  const meta = PRACTICE_SECTION_META[module];
  const icon: LucideIcon = meta.icon;

  return (
    <Link
      to="/progress/$skill"
      params={{ skill: module }}
      search={{ variant: undefined }}
      className="focus-ring rounded-card border border-border bg-surface p-5 no-underline transition-shadow hover:shadow-e1"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className={MODULE_TONE[module]}>
            <Icon icon={icon} size={22} />
          </span>
          <p className="mt-2 font-display text-h4 text-ink">{meta.label}</p>
          <p className="text-small text-muted">
            {attempts === 0 ? 'Not taken yet' : `${attempts} test${attempts === 1 ? '' : 's'}`}
          </p>
        </div>
        {loading ? (
          <Skeleton className="size-12 rounded-[12px]" />
        ) : (
          <BandValue value={latest?.value ?? null} size="sm" />
        )}
      </div>
      {latest ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Pill tone="neutral">
            {latest.kind === 'estimated_ai' ? 'AI estimate' : 'Marked by AI'}
          </Pill>
          {latest.range ? (
            <span className="text-small text-ink-2">
              {formatBand(latest.range[0])} to {formatBand(latest.range[1])}
            </span>
          ) : null}
          {target === null ? null : (
            <span className="text-small font-bold text-ink-2">
              {latest.value >= target
                ? 'On target'
                : `${formatBand(Math.round((target - latest.value) * 2) / 2)} to target`}
            </span>
          )}
        </div>
      ) : null}
    </Link>
  );
}
